import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Helmet from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FeatureAccessIndicator from '../../components/ui/FeatureAccessIndicator';
import AdSense from '../../components/AdSense';

import AdvancementReadinessCard from './components/AdvancementReadinessCard';
import TimingRecommendations from './components/TimingRecommendations';
import NegotiationScenarios from './components/NegotiationScenarios';
import ProbabilityCalculator from './components/ProbabilityCalculator';
import StrategyPlanner from './components/StrategyPlanner';
import MarketInsights from './components/MarketInsights';
import { generateAdvancementGuidance, generateNegotiationScenarios, calculateAdvancementProbability } from '../../services/careerAdvancementService';
import { loadAssessmentProgress } from '../../services/assessmentService';
import Footer from '../../components/Footer';
import { useAuth } from '../../contexts/AuthContext';

const CareerAdvancementGuidance = () => {
  const { user, userProfile, isPremium, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [guidanceData, setGuidanceData] = useState(null);
  const [scenarioData, setScenarioData] = useState(null);
  const [probabilityData, setProbabilityData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [hasCompletedAssessment, setHasCompletedAssessment] = useState(false);
  const [assessmentData, setAssessmentData] = useState(null);

  const navigate = useNavigate();

  // Add missing function declarations
  const handleAuthRequired = () => {
    navigate('/login');
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  useEffect(() => {
    const initializeComponent = async () => {
      setLoading(true);
      setError(null);
      
      if (!user) {
        setLoading(false);
        return;
      }

      // Check premium access
      if (!isPremium) {
        setLoading(false);
        return;
      }

      try {
        // Check if user has completed assessment
        const assessmentResult = await loadAssessmentProgress(user.id);
        
        if (!assessmentResult.success || !assessmentResult.data || !assessmentResult.data.is_completed) {
          setHasCompletedAssessment(false);
          setLoading(false);
          return;
        }

        // User has completed assessment
        setHasCompletedAssessment(true);
        setAssessmentData(assessmentResult.data);

        // Generate initial guidance based on real assessment data
        await Promise.race([
          generateInitialGuidance(user, assessmentResult.data),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Loading timeout')), 10000)
          )
        ]);
      } catch (error) {
        console.error('Error initializing career guidance:', error);
        setError('Failed to load career guidance. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeComponent();
  }, [user, isPremium]);

  const generateInitialGuidance = async (userData, assessmentData) => {
    setIsGenerating(true);
    setError(null);

    try {
      // Extract real data from assessment
      const assessmentSetup = assessmentData?.assessment_setup || {};
      const answers = assessmentData?.answers || {};
      
      // Build user profile from real assessment data
      const userProfile = {
        occupation: assessmentSetup?.occupation || userData?.occupation || 'Software Engineer',
        seniority: assessmentSetup?.seniority || userData?.seniority || 'Mid-Level',
        currentSalary: '$85,000', // Could be enhanced with salary insights data
        lastPromotion: '18 months ago', // Could be enhanced with user input
        lastRaise: '12 months ago', // Could be enhanced with user input
        currentPerformance: 'Exceeds Expectations', // Could be enhanced with assessment answers
        marketConditions: 'Strong', // Could be enhanced with market data
        companySize: 'Medium (500-1000 employees)', // Could be enhanced with user input
        tenure: '2.5 years', // Could be enhanced with user input
        achievements: [
          'Led successful project delivery',
          'Mentored 2 junior developers',
          'Improved team efficiency by 25%'
        ],
        skillsAcquired: [
          'Advanced system design',
          'Team leadership',
          'Stakeholder management'
        ],
        userId: userData?.id,
        // Add assessment-specific data
        assessmentAnswers: answers,
        locationData: {
          currentZipCode: assessmentSetup?.currentZipCode,
          locationPreference: assessmentSetup?.locationPreference,
          targetLocation: assessmentSetup?.targetLocation
        }
      };

      // Generate guidance from real assessment data
      try {
        const guidance = await generateAdvancementGuidance(userProfile);
        setGuidanceData(guidance);
      } catch (guidanceError) {
        console.error('Guidance service failed:', guidanceError);
        throw new Error('Failed to generate career advancement guidance. Please try again.');
      }

      // Generate probability analysis from real assessment data
      try {
        const probabilityFactors = {
          performanceRating: answers?.[2] ? `${answers[2]}/5` : 'Unknown',
          tenure: 'Unknown', // Could be enhanced with user input
          marketConditions: 'Unknown', // Could be enhanced with market data
          budgetCycle: 'Unknown', // Could be enhanced with user input
          recentAchievements: userProfile?.achievements || [],
          skillGaps: answers?.[3] || [],
          companyGrowth: 'Unknown', // Could be enhanced with user input
          competitionLevel: 'Unknown', // Could be enhanced with market data
          userId: userData?.id
        };

        const probability = await calculateAdvancementProbability(probabilityFactors);
        setProbabilityData(probability);
      } catch (probabilityError) {
        console.error('Probability service failed:', probabilityError);
        throw new Error('Failed to generate advancement probability analysis. Please try again.');
      }
    } catch (error) {
      console.error('Error generating initial guidance:', error);
      setError(error?.message || 'Failed to generate career advancement guidance');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleGenerateScenarios = async (type) => {
    setIsGenerating(true);
    setError(null);

    try {
      const negotiationContext = {
        type: type, // 'promotion' or 'raise'
        currentRole: user?.occupation || 'Software Engineer',
        targetRole: type === 'promotion' ? 'Senior Software Engineer' : undefined,
        currentSalary: '$85,000',
        targetSalary: type === 'raise' ? '$95,000' : undefined,
        justifications: [
          'Consistent high performance',
          'Additional responsibilities taken on',
          'Market rate alignment'
        ],
        managerType: 'Data-driven',
        companyBudget: 'Healthy',
        userId: user?.id
      };

      const scenarios = await generateNegotiationScenarios(negotiationContext);
      setScenarioData(scenarios);
      setActiveTab('scenarios');
    } catch (error) {
      console.error('Error generating scenarios:', error);
      setError(error?.message || 'Failed to generate negotiation scenarios');
    } finally {
      setIsGenerating(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'BarChart3' },
    { id: 'timing', label: 'Timing', icon: 'Clock' },
    { id: 'scenarios', label: 'Scenarios', icon: 'MessageSquare' },
    { id: 'probability', label: 'Success Rate', icon: 'TrendingUp' },
    { id: 'strategy', label: 'Strategy', icon: 'Target' },
    { id: 'market', label: 'Market Insights', icon: 'Globe' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Loading career advancement guidance...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Career Advancement Guidance - CareerLift</title>
        <meta name="description" content="Get personalized career advancement guidance, strategic planning, and professional growth recommendations with CareerLift's AI-powered insights." />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header onAuthRequired={handleAuthRequired} onLogout={handleLogout} />
        
        <main className="flex-1 pt-32 pb-16" style={{ paddingTop: 'calc(8rem + 30px)' }}>
          <div className="max-w-5xl mx-auto">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {/* Page Header */}
              <div className="text-center mb-2">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Icon name="TrendingUp" size={32} className="text-primary" />
                  <h1 className="text-3xl font-bold text-foreground">
                    Career Advancement Guidance
                  </h1>
                </div>
                <p className="text-muted-foreground">
                  AI-powered insights for promotion and salary advancement timing
                </p>
              </div>

              {/* Page Audio */}
              <div className="flex justify-center mb-2">
                <audio
                  src="/assets/audio/Career_Advancement_Guidance.mp3"
                  controls
                  preload="metadata"
                  className="h-8 max-w-xs"
                />
              </div>

              {/* AdSense - Banner for Free Users */}
              {!isPremium && (
                <div className="mb-2">
                  <AdSense 
                    adSlot="1234567890"
                    adFormat="auto"
                    className="w-full"
                  />
                </div>
              )}

              <div className="space-y-8">

          {/* Premium Access Check */}
          {user && !isPremium && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <FeatureAccessIndicator 
                user={user}
                feature="ai-plans"
                showUpgradePrompt={true}
                size="lg"
                className="text-center"
              />
            </div>
          )}

          {/* Assessment Completion Check */}
          {user && isPremium && !hasCompletedAssessment && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={32} className="text-primary" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground mb-3">
                  Complete Your Assessment First
                </h2>
                <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                  Career Guidance requires your completed assessment to provide personalized, data-driven insights. 
                  Complete your career assessment to unlock AI-powered guidance tailored to your specific role, 
                  experience level, and career goals.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate('/career-assessment-setup')}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Icon name="Play" size={16} className="mr-2" />
                    Start Assessment
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate('/progress-dashboard')}
                  >
                    <Icon name="BarChart3" size={16} className="mr-2" />
                    View Dashboard
                  </Button>
                </div>
                <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold text-foreground mb-2">What you'll get with Career Guidance:</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                    <div className="flex items-center space-x-2">
                      <Icon name="Check" size={14} className="text-success" />
                      <span>Personalized promotion timing</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Check" size={14} className="text-success" />
                      <span>Salary negotiation strategies</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Check" size={14} className="text-success" />
                      <span>Success probability analysis</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Check" size={14} className="text-success" />
                      <span>Market insights & benchmarks</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          {user && isPremium && hasCompletedAssessment && (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              {/* Navigation Tabs */}
              <div className="mb-2">
                <div className="border-b border-border">
                  <nav className="-mb-px flex space-x-8 overflow-x-auto">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                          activeTab === tab?.id
                            ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'
                        }`}
                      >
                        <Icon name={tab?.icon} size={16} />
                        <span>{tab?.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mb-6 bg-destructive/10 border border-destructive/20 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertCircle" size={16} className="text-destructive" />
                    <p className="text-destructive text-sm">{error}</p>
                  </div>
                </div>
              )}

              {/* Loading State */}
              {isGenerating && (
                <div className="mb-6 bg-primary/10 border border-primary/20 rounded-lg p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-primary text-sm">Generating AI-powered insights...</p>
                  </div>
                </div>
              )}

              {/* Tab Content */}
              <div className="space-y-6">
                {activeTab === 'overview' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <AdvancementReadinessCard 
                      data={guidanceData}
                      onGenerateScenarios={handleGenerateScenarios}
                      loading={isGenerating}
                    />
                    <div className="space-y-6">
                      <div className="bg-card border border-border rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center space-x-2">
                          <Icon name="Target" size={20} />
                          <span>Quick Actions</span>
                        </h3>
                        <div className="space-y-3">
                          <Button
                            variant="outline"
                            size="sm"
                            fullWidth
                            iconName="MessageSquare"
                            iconPosition="left"
                            onClick={() => handleGenerateScenarios('promotion')}
                            disabled={isGenerating}
                          >
                            Generate Promotion Strategy
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            fullWidth
                            iconName="DollarSign"
                            iconPosition="left"
                            onClick={() => handleGenerateScenarios('raise')}
                            disabled={isGenerating}
                          >
                            Generate Salary Negotiation
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            fullWidth
                            iconName="TrendingUp"
                            iconPosition="left"
                            onClick={() => setActiveTab('probability')}
                          >
                            View Success Probability
                          </Button>
                        </div>
                      </div>

                      {/* Premium Features Showcase - Hidden for premium users */}
                      {!isPremium && (
                        <div className="bg-gradient-to-br from-warning/10 to-primary/10 border border-warning/30 rounded-lg p-6">
                          <h3 className="text-lg font-semibold text-foreground mb-3 flex items-center space-x-2">
                            <Icon name="Crown" size={20} className="text-warning" />
                            <span>Premium Features Available</span>
                          </h3>
                          <div className="space-y-2 text-sm">
                            {[
                              'AI-powered timing recommendations',
                              'Personalized negotiation scripts',
                              'Success probability calculator',
                              'Market salary benchmarks',
                              'Strategy planning tools'
                            ]?.map((feature, index) => (
                              <div key={index} className="flex items-center space-x-2 text-muted-foreground">
                                <Icon name="Check" size={14} className="text-success" />
                                <span>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'timing' && (
                  <TimingRecommendations 
                    data={guidanceData}
                    loading={isGenerating}
                  />
                )}

                {activeTab === 'scenarios' && (
                  <NegotiationScenarios 
                    data={scenarioData}
                    onGenerateScenarios={handleGenerateScenarios}
                    loading={isGenerating}
                  />
                )}

                {activeTab === 'probability' && (
                  <ProbabilityCalculator 
                    data={probabilityData}
                    loading={isGenerating}
                  />
                )}

                {activeTab === 'strategy' && (
                  <StrategyPlanner 
                    data={guidanceData}
                    loading={isGenerating}
                  />
                )}

                {activeTab === 'market' && (
                  <MarketInsights 
                    data={guidanceData}
                    user={user}
                    loading={isGenerating}
                  />
                )}
              </div>

              {/* Bottom Navigation for Mobile */}
              <div className="lg:hidden mt-8">
                <div className="bg-card border border-border rounded-lg p-4">
                  <h3 className="font-semibold text-foreground mb-4">Related Actions</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="BarChart3"
                      iconPosition="left"
                      fullWidth
                      asChild
                    >
                      <Link to="/progress-dashboard">View Progress</Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="FileText"
                      iconPosition="left"
                      fullWidth
                      asChild
                    >
                      <Link to="/ai-plan-generation">Career Plan</Link>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="Play"
                      iconPosition="left"
                      fullWidth
                      asChild
                    >
                      <Link to="/exercise-management">Exercises</Link>
                    </Button>
                    {!isPremium && (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="Crown"
                        iconPosition="left"
                        fullWidth
                        asChild
                      >
                        <Link to="/subscription-management">Upgrade</Link>
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
            </div>
          </div>
        </div>
        </main>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default CareerAdvancementGuidance;